import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Star, MapPin, Package, Video, Clock, CheckCircle, ExternalLink } from 'lucide-react';
import {
  getVendorConsultationSettings,
  getClientConsultationWithVendor,
} from '../../services/consultationService';
import BookConsultationModal from './BookConsultationModal';
import {
  getBrandFromVendor,
  getBrandButtonStyle,
  getBrandCtaLabel,
  isBrandActive,
  darkenColor,
} from '../../utils/vendorBranding';

const formatConsultationDate = (date) =>
  date
    ? new Date(date).toLocaleDateString('en-US', {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      })
    : null;

const formatConsultationTime = (time) => {
  if (!time) return null;
  const parts = String(time).split(':');
  const h = parseInt(parts[0] || 0, 10);
  const m = parts[1] || '00';
  const ampm = h >= 12 ? 'PM' : 'AM';
  const h12 = h > 12 ? h - 12 : h === 0 ? 12 : h;
  return `${h12}:${m} ${ampm}`;
};

const VendorBookingWidget = ({ vendor, services, minPrice, isAuthenticated, brand: brandProp }) => {
  const queryClient = useQueryClient();
  const [showConsultationModal, setShowConsultationModal] = useState(false);
  const brand = brandProp || getBrandFromVendor(vendor);
  const brandActive = isBrandActive(brand);
  const primaryBtnStyle = getBrandButtonStyle(brand, 'primary');
  const outlineBtnStyle = getBrandButtonStyle(brand, 'outline');
  const ctaLabel = getBrandCtaLabel(brand, services.length > 0 ? 'View Services' : 'Contact Vendor');
  const vendorId = vendor?.id;
  const { data: consultationData } = useQuery({
    queryKey: ['vendorConsultationSettings', vendorId],
    queryFn: () => getVendorConsultationSettings(vendorId),
    enabled: !!vendorId,
  });
  const { data: activeConsultationRes } = useQuery({
    queryKey: ['clientConsultationWithVendor', vendorId],
    queryFn: () => getClientConsultationWithVendor(vendorId),
    enabled: !!vendorId && isAuthenticated,
  });
  const consultationEnabled = consultationData?.data?.consultation_enabled ?? false;
  const consultationQuestions = consultationData?.data?.consultation_questions ?? [];
  const activeConsultation = activeConsultationRes?.data ?? null;
  const cheapestService = services.length > 0 ? services.reduce((prev, current) => 
    (prev.base_price || 0) < (current.base_price || 0) ? prev : current
  ) : null;

  const vendorRating = Number(vendor?.average_rating) || 0;
  const vendorReviews = Number(vendor?.total_reviews) || 0;

  return (
    <div className="border border-gray-200 rounded-xl p-6 bg-white shadow-lg">
      {/* Price */}
      <div className="mb-6">
        <div className="flex items-baseline gap-2">
          <span className="text-2xl font-light text-black">
            ${minPrice ? minPrice.toFixed(2) : cheapestService?.base_price || 'Contact'}
          </span>
          {services.length > 0 && minPrice && (
            <span className="text-base text-gray-600 font-light">starting price</span>
          )}
        </div>
      </div>

      {/* Service Summary */}
      {services.length > 0 && (
        <div className="mb-6 p-4 border border-gray-200 rounded-lg space-y-3">
          <div className="flex items-center justify-between pb-3 border-b border-gray-200">
            <span className="text-sm font-light text-gray-600">Services</span>
            <div className="flex items-center gap-1">
              <Package className="w-4 h-4 text-gray-600" />
              <span className="text-sm font-medium text-black">{services.length} available</span>
            </div>
          </div>
          <div className="text-sm text-gray-700 font-light">
            Browse services to view pricing and book
          </div>
        </div>
      )}

      {/* CTA Button */}
      <Link
        to={cheapestService ? `/services/${cheapestService.id}` : '#'}
        className={`block w-full py-4 px-4 text-center rounded-lg font-light transition-colors mb-3 ${
          brandActive ? '' : 'bg-black text-white hover:bg-gray-800'
        }`}
        style={brandActive ? primaryBtnStyle : undefined}
        onMouseEnter={(e) => {
          if (brandActive) {
            e.currentTarget.style.backgroundColor = darkenColor(brand.primary_color);
          }
        }}
        onMouseLeave={(e) => {
          if (brandActive && primaryBtnStyle) {
            e.currentTarget.style.backgroundColor = primaryBtnStyle.backgroundColor;
          }
        }}
      >
        {ctaLabel}
      </Link>

      {consultationEnabled && activeConsultation?.status === 'pending' && (
        <div className="mb-3 rounded-xl border border-amber-200 bg-gradient-to-br from-amber-50 to-orange-50 p-4">
          <div className="flex gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-amber-100">
              <Clock className="h-5 w-5 text-amber-700" />
            </div>
            <div className="min-w-0">
              <p className="text-sm font-medium text-amber-950">Consultation request pending</p>
              <p className="mt-1 text-xs leading-relaxed text-amber-900/90">
                You&apos;ve already requested a consultation with{' '}
                <span className="font-medium">{vendor?.business_name || 'this vendor'}</span>
                {activeConsultation.scheduled_date && (
                  <>
                    {' '}
                    for{' '}
                    <span className="font-medium">
                      {formatConsultationDate(activeConsultation.scheduled_date)}
                      {activeConsultation.scheduled_time
                        ? ` at ${formatConsultationTime(activeConsultation.scheduled_time)}`
                        : ''}
                    </span>
                  </>
                )}
                . We&apos;ll email you when they accept and share the meeting link.
              </p>
            </div>
          </div>
        </div>
      )}

      {consultationEnabled && activeConsultation?.status === 'accepted' && (
        <div className="mb-3 rounded-xl border border-emerald-200 bg-gradient-to-br from-emerald-50 to-teal-50 p-4">
          <div className="flex gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-emerald-100">
              <CheckCircle className="h-5 w-5 text-emerald-700" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium text-emerald-950">Consultation confirmed</p>
              <p className="mt-1 text-xs leading-relaxed text-emerald-900/90">
                Your consultation is scheduled
                {activeConsultation.scheduled_date && (
                  <>
                    {' '}
                    for{' '}
                    <span className="font-medium">
                      {formatConsultationDate(activeConsultation.scheduled_date)}
                      {activeConsultation.scheduled_time
                        ? ` at ${formatConsultationTime(activeConsultation.scheduled_time)}`
                        : ''}
                    </span>
                  </>
                )}
                . Check your email for details.
              </p>
              {activeConsultation.google_meet_link && (
                <a
                  href={activeConsultation.google_meet_link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-2 inline-flex items-center gap-1 text-xs font-medium text-emerald-800 hover:underline"
                >
                  <ExternalLink className="h-3.5 w-3.5" />
                  Join meeting
                </a>
              )}
            </div>
          </div>
        </div>
      )}

      {consultationEnabled && !activeConsultation && (
        <button
          type="button"
          onClick={() => {
            setShowConsultationModal(true);
          }}
          className={`w-full py-3 px-4 border-2 text-center rounded-lg font-light transition-colors mb-3 flex items-center justify-center gap-2 ${
            brandActive ? '' : 'border-gray-900 text-gray-900 hover:bg-gray-50'
          }`}
          style={brandActive ? outlineBtnStyle : undefined}
        >
          <Video className="w-4 h-4" />
          Book Consultation
        </button>
      )}

      <p className="text-xs text-center text-gray-500 font-light mb-6">
        {/* You won't be charged yet */}
      </p>

      <BookConsultationModal
        isOpen={showConsultationModal}
        onClose={() => setShowConsultationModal(false)}
        onSuccess={() => {
          queryClient.invalidateQueries({ queryKey: ['clientConsultationWithVendor', vendorId] });
        }}
        vendorId={vendorId}
        vendorName={vendor?.business_name}
        serviceId={null}
        questions={consultationQuestions}
      />

      {/* Details Section */}
      <div className="pt-6 border-t border-gray-200">
        <div className="space-y-4 text-sm">
          <div className="flex items-center justify-between">
            <span className="text-gray-600 font-light">Starting Price</span>
            <span className="font-light text-black">
              ${minPrice ? minPrice.toFixed(2) : 'Contact'}
            </span>
          </div>

          {services.length > 0 && (
            <div className="flex items-center justify-between">
              <span className="text-gray-600 font-light">Services Available</span>
              <div className="flex items-center gap-1">
                <Package className="w-4 h-4 text-gray-600" />
                <span className="font-light text-black">{services.length}</span>
              </div>
            </div>
          )}

          {vendorRating > 0 && (
            <div className="flex items-center justify-between">
              <span className="text-gray-600 font-light">Rating</span>
              <div className="flex items-center gap-1">
                <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                <span className="font-light text-black">
                  {typeof vendorRating === 'number' && !isNaN(vendorRating) ? vendorRating.toFixed(1) : '0.0'}
                  {vendorReviews > 0 && (
                    <span className="text-gray-500 ml-1">
                      ({vendorReviews})
                    </span>
                  )}
                </span>
              </div>
            </div>
          )}

          {vendor?.city && vendor?.state && (
            <div className="flex items-center justify-between">
              <span className="text-gray-600 font-light">Location</span>
              <div className="flex items-center gap-1">
                <MapPin className="w-4 h-4 text-gray-600" />
                <span className="font-light text-black">
                  {vendor.city}, {vendor.state}
                </span>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Report Listing */}
      {/* <div className="pt-6 border-t border-gray-200">
        <button className="w-full text-sm font-light text-black underline text-center hover:text-gray-600 transition-colors">
          Report this listing
        </button>
      </div> */}
    </div>
  );
};

export default VendorBookingWidget;

